/*****************************************************
Naam: CustomerTracking.jsx
Locatie: src/pages/CustomerTracking.jsx
Datum: 2026-01-09
Versie: 1.0.0
******************************************************
BESCHRIJVING:
Customer tracking pagina - overzicht van eigen aanvragen.
Filtered view zonder vervoerder informatie.

FEATURES:
- Tabel met eigen aanvragen
- Status filtering
- Search functionaliteit
- Geen vervoerder info (security)
- Responsive design
******************************************************/

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, 
  Search, 
  Filter,
  Calendar,
  MapPin,
  ChevronLeft,
  TrendingUp,
  Clock,
  CheckCircle,
  Truck
} from 'lucide-react';

const CustomerTracking = () => {
  const navigate = useNavigate();
  const [aanvragen, setAanvragen] = useState([]);
  const [filteredAanvragen, setFilteredAanvragen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    checkAuth();
    loadAanvragen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, aanvragen]);

  const checkAuth = async () => {
    try {
      const response = await fetch('/api/customer/check-auth');
      const data = await response.json();
      
      if (!data.authenticated) {
        navigate('/customer/login');
      }
    } catch (err) {
      console.error('Auth check error:', err);
      navigate('/customer/login');
    }
  };

  const loadAanvragen = async () => {
    try {
      const response = await fetch('/api/customer/aanvragen');
      const data = await response.json();
      
      if (data.success) {
        setAanvragen(data.aanvragen || []);
        setFilteredAanvragen(data.aanvragen || []);
      }
    } catch (err) {
      console.error('Load aanvragen error:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...aanvragen];

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(a => {
        const status = (a.Status || '').toLowerCase();
        return status.includes(statusFilter.toLowerCase());
      });
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(a => 
        (a.AanvraagID?.toString() || '').includes(term) ||
        (a.LaadAdres || '').toLowerCase().includes(term) ||
        (a.LosAdres || '').toLowerCase().includes(term) ||
        (a.LaadLand || '').toLowerCase().includes(term) ||
        (a.LosLand || '').toLowerCase().includes(term) ||
        (a.Referentie1 || '').toLowerCase().includes(term) ||
        (a.Referentie2 || '').toLowerCase().includes(term)
      );
    }

    setFilteredAanvragen(filtered);
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('afgeleverd')) return 'bg-green-100 text-green-800';
    if (s.includes('onderweg')) return 'bg-orange-100 text-orange-800';
    if (s.includes('bevestigd')) return 'bg-blue-100 text-blue-800';
    if (s.includes('nieuw')) return 'bg-blue-100 text-blue-900';
    return 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('afgeleverd')) return <CheckCircle className="w-4 h-4" />;
    if (s.includes('onderweg')) return <Truck className="w-4 h-4" />;
    if (s.includes('nieuw')) return <Clock className="w-4 h-4" />;
    return <TrendingUp className="w-4 h-4" />;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return new Date(date).toLocaleDateString('nl-NL');
    } catch {
      return date;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/customer/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-none transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-gray-700" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Tracking</h1>
                <p className="text-sm text-gray-600">
                  {filteredAanvragen.length} van {aanvragen.length} aanvragen
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search & Filters */}
        <div className="bg-white rounded-none shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Zoek op ID, locatie, referentie..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-none focus:ring-2 focus:ring-blue-300 focus:border-transparent"
              />
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-6 py-3 rounded-none font-medium transition-all ${
                showFilters
                  ? 'bg-blue-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter className="w-5 h-5" />
              <span>Filters</span>
            </button>
          </div>

          {/* Status Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex flex-wrap gap-2">
                {['all', 'nieuw', 'bevestigd', 'onderweg', 'afgeleverd'].map(status => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-4 py-2 rounded-none font-medium transition-all ${
                      statusFilter === status
                        ? 'bg-blue-900 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {status === 'all' ? 'Alle' : status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Aanvragen Table */}
        {filteredAanvragen.length === 0 ? (
          <div className="bg-white rounded-none shadow-lg p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Geen aanvragen gevonden
            </h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter !== 'all' 
                ? 'Probeer andere zoekfilters'
                : 'Er zijn nog geen aanvragen'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-none shadow-lg overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Datum
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Laden
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Lossen
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Prijs
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredAanvragen.map((aanvraag, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-semibold text-blue-900">
                          #{aanvraag.AanvraagID}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {formatDate(aanvraag.Datum)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">{aanvraag.LaadAdres}</div>
                            <div className="text-gray-500">{aanvraag.LaadLand}</div>
                            <div className="text-gray-400 text-xs">{formatDate(aanvraag.LaadDatum)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">{aanvraag.LosAdres}</div>
                            <div className="text-gray-500">{aanvraag.LosLand}</div>
                            <div className="text-gray-400 text-xs">{formatDate(aanvraag.LosDatum)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(aanvraag.Status)}`}>
                          {getStatusIcon(aanvraag.Status)}
                          {aanvraag.Status || 'Onbekend'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-semibold text-gray-900">
                          {aanvraag.KlantPrijs ? `€ ${aanvraag.KlantPrijs}` : '-'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden divide-y divide-gray-200">
              {filteredAanvragen.map((aanvraag, index) => (
                <div key={index} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-blue-900">
                      #{aanvraag.AanvraagID}
                    </span>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(aanvraag.Status)}`}>
                      {getStatusIcon(aanvraag.Status)}
                      {aanvraag.Status}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      {formatDate(aanvraag.Datum)}
                    </div>

                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-green-500 mt-0.5" />
                      <div>
                        <div className="font-medium">{aanvraag.LaadAdres}</div>
                        <div className="text-gray-500">{aanvraag.LaadLand} - {formatDate(aanvraag.LaadDatum)}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-red-500 mt-0.5" />
                      <div>
                        <div className="font-medium">{aanvraag.LosAdres}</div>
                        <div className="text-gray-500">{aanvraag.LosLand} - {formatDate(aanvraag.LosDatum)}</div>
                      </div>
                    </div>

                    {aanvraag.KlantPrijs && (
                      <div className="pt-2 border-t border-gray-200">
                        <span className="font-semibold text-gray-900">
                          Prijs: € {aanvraag.KlantPrijs}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerTracking;
